import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, MapPin, Grid3x3, List } from 'lucide-react';

const CATEGORIES = [
  'Academia e Esporte', 'Beleza Feminina', 'Beleza Masculina', 'Estética e Bem-estar',
  'Moda e Acessórios', 'Infantil e RN', 'Saúde', 'Saúde Veterinária',
  'Pet Shop', 'Casa e Decoração', 'Construção e Reforma', 'Eletrônicos e Tecnologia',
  'Veículos Elétricos', 'Carro e Motos', 'Gastronomia e Bebidas', 'Manutenção e Reparos',
  'Lazer e Diversão', 'Educação e Ensino'
];

export const InfluencerBrowseTab = ({ allCoupons, userCity, navigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState();
  const [stateFilter, setStateFilter] = useState();
  const [categoryFilter, setCategoryFilter] = useState();
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    if (userCity) setCityFilter(userCity);
  }, [userCity]);

  const filteredCoupons = allCoupons.filter(coupon => {
    const matchesSearch = !searchTerm || coupon.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         coupon.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCity = !cityFilter || cityFilter === 'all' || coupon.city === cityFilter;
    const matchesCategory = !categoryFilter || categoryFilter === 'all' || coupon.category === categoryFilter;
    const matchesState = !stateFilter || stateFilter === 'all' || coupon.state === stateFilter;
    return matchesSearch && matchesCity && matchesCategory && matchesState;
  }).sort((a, b) => {
    if (sortBy === 'highest_discount') return b.discount_percentage - a.discount_percentage;
    if (sortBy === 'lowest_discount') return a.discount_percentage - b.discount_percentage;
    if (sortBy === 'expiring_soon') return new Date(a.expires_at) - new Date(b.expires_at);
    return 0;
  });

  const cities = [...new Set(allCoupons.map(c => c.city).filter(Boolean))];
  const states = [...new Set(allCoupons.map(c => c.state).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="influencer-coupon-browser-section">
      <Card className="hero-card">
        <CardHeader>
          <CardTitle>Buscar Cupons</CardTitle>
          <CardDescription className="text-slate-300">Filtre por categoria, cidade ou busca livre</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Buscar cupons..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="inf-coupon-search-input" />
            </div>
            <Select value={cityFilter || undefined} onValueChange={setCityFilter}>
              <SelectTrigger data-testid="inf-city-filter"><SelectValue placeholder="Todas as cidades" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as cidades</SelectItem>
                {cities.map(city => (<SelectItem key={city} value={city}>{city}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={stateFilter || undefined} onValueChange={setStateFilter}>
              <SelectTrigger data-testid="inf-state-filter"><SelectValue placeholder="Todos os estados" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os estados</SelectItem>
                {states.map(state => (<SelectItem key={state} value={state}>{state}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter || undefined} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="inf-category-filter"><SelectValue placeholder="Todas as categorias" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                {CATEGORIES.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="inf-sort-filter"><SelectValue placeholder="Ordenar por" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Mais Recente</SelectItem>
                <SelectItem value="highest_discount">Maior Desconto</SelectItem>
                <SelectItem value="lowest_discount">Menor Desconto</SelectItem>
                <SelectItem value="expiring_soon">Prazo que Expira</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-slate-900">Cupons Disponíveis</h2>
          <div className="flex gap-2">
            <Button variant={viewMode === 'grid' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'bg-slate-600 hover:bg-slate-700 border-slate-600' : 'border-slate-400 hover:bg-slate-100'} data-testid="inf-grid-view-btn">
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button variant={viewMode === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-slate-600 hover:bg-slate-700 border-slate-600' : 'border-slate-400 hover:bg-slate-100'} data-testid="inf-list-view-btn">
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {filteredCoupons.length === 0 ? (
          <Card className="hero-card"><CardContent className="py-12 text-center"><p className="text-slate-200">Nenhum cupom encontrado</p></CardContent></Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCoupons.map(coupon => (
              <Card key={coupon.coupon_id} className="coupon-card hero-card cursor-pointer hover:border-slate-400 hover:shadow-lg transition-all"
                onClick={() => navigate(`/coupons/${coupon.coupon_id}`)} data-testid={`inf-coupon-${coupon.coupon_id}`}>
                {coupon.images?.length > 0 && (
                  <div className="w-full aspect-[4/3] overflow-hidden rounded-t-md">
                    <img src={coupon.images[0]} alt={coupon.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <Badge className="bg-white/20 text-white border-white/30">{coupon.discount_percentage}% OFF</Badge>
                    <Badge className="bg-white/20 text-white border-white/30 text-xs">{coupon.category}</Badge>
                  </div>
                  <CardTitle className="text-xl text-white">{coupon.title}</CardTitle>
                  <CardDescription className="line-clamp-2 text-slate-200">{coupon.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-200">
                      <MapPin className="h-4 w-4" /> {coupon.city}, {coupon.state}
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-slate-300">Expira: {new Date(coupon.expires_at).toLocaleDateString('pt-BR')}</p>
                      {coupon.max_uses === 1 && <Badge className="bg-red-500/80 text-white border-transparent text-xs">Uso Único</Badge>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {filteredCoupons.map(coupon => (
              <Card key={coupon.coupon_id} className="coupon-card hero-card cursor-pointer hover:border-slate-400 hover:shadow-lg transition-all"
                onClick={() => navigate(`/coupons/${coupon.coupon_id}`)} data-testid={`inf-coupon-list-${coupon.coupon_id}`}>
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    {coupon.images?.length > 0 && (
                      <div className="w-24 h-24 overflow-hidden rounded-md border border-white/20 flex-shrink-0">
                        <img src={coupon.images[0]} alt={coupon.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className="bg-white/20 text-white border-white/30">{coupon.discount_percentage}% OFF</Badge>
                        <Badge className="bg-white/20 text-white border-white/30 text-xs">{coupon.category}</Badge>
                        {coupon.max_uses === 1 && <Badge className="bg-red-500/80 text-white border-transparent text-xs">Uso Único</Badge>}
                      </div>
                      <h3 className="text-xl font-bold text-white mb-1">{coupon.title}</h3>
                      <p className="text-sm text-slate-200 mb-3">{coupon.description}</p>
                      <div className="flex flex-wrap gap-4 text-xs text-slate-300">
                        <div className="flex items-center gap-1"><MapPin className="h-3 w-3" /><span>{coupon.city}, {coupon.state}</span></div>
                        <span>Expira: {new Date(coupon.expires_at).toLocaleDateString('pt-BR')}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
